import { Button } from '@/components/ui/button.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Label } from '@/components/ui/label.tsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MaskedSettings } from '@ozon-ai/apigateway/contracts';
import type { PurchaseResponse } from '@ozon-ai/procurement/contracts';
import { api } from '../api/client';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PurchasesListGrid } from '../components/PurchasesListGrid';
import { useTelegram } from '../context/TelegramContext';
import { ErrorText } from '../ui/PageShell';
import { GridPage } from '../ui/GridPage';
import { ContentCard } from '../ui/ContentCard';

export function PurchasesPage() {
  const { webApp } = useTelegram();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState<PurchaseResponse[]>([]);
  const [hideInactive, setHideInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await api.get<PurchaseResponse[]>('/purchases');
      setPurchases(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить закупки');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      setError(null);
      try {
        const [settings, data] = await Promise.all([
          api.get<MaskedSettings>('/settings'),
          api.get<PurchaseResponse[]>('/purchases'),
        ]);
        if (cancelled) return;
        setHideInactive(Boolean(settings?.public.purchases?.hideInactive));
        setPurchases(data);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Не удалось загрузить закупки');
        setHideInactive(false);
        setPurchases([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const visiblePurchases = useMemo(() => {
    const sorted = purchases.slice().reverse();
    if (!hideInactive) {
      return sorted;
    }
    return sorted.filter((purchase) => !purchase.inactive);
  }, [purchases, hideInactive]);

  async function toggleHideInactive(checked: boolean) {
    setHideInactive(checked);
    try {
      await api.put('/settings/public', {
        purchases: { hideInactive: checked },
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось сохранить настройку');
    }
  }

  async function createPurchase() {
    setBusy(true);
    try {
      const created = await api.post<PurchaseResponse>('/purchases', {});
      webApp?.HapticFeedback?.notificationOccurred('success');
      navigate(`/purchases/${encodeURIComponent(created.id)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка создания');
    } finally {
      setBusy(false);
    }
  }

  const openPurchase = useCallback(
    (purchase: PurchaseResponse) => {
      navigate(`/purchases/${encodeURIComponent(purchase.id)}`);
    },
    [navigate],
  );

  const deactivatePurchaseFromList = useCallback(
    async (purchase: PurchaseResponse) => {
      setBusy(true);
      setError(null);
      try {
        await api.delete<PurchaseResponse>(`/purchases?id=${encodeURIComponent(purchase.id)}`);
        webApp?.HapticFeedback?.notificationOccurred('success');
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Ошибка деактивации');
      } finally {
        setBusy(false);
      }
    },
    [load, webApp],
  );

  if (loading) return <LoadingSpinner />;

  return (
    <GridPage
      subheader={
        <>
          {error ? <ErrorText>{error}</ErrorText> : null}
          <ContentCard className="mb-0 gap-0 py-0" contentClassName="p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="hideInactive"
                  checked={hideInactive}
                  onCheckedChange={(checked) => void toggleHideInactive(checked === true)}
                />
                <Label htmlFor="hideInactive">Скрыть неактивные</Label>
              </div>
              <Button type="button" disabled={busy} onClick={() => void createPurchase()}>
                Создать черновик
              </Button>
            </div>
          </ContentCard>
        </>
      }
    >
      <PurchasesListGrid
        purchases={visiblePurchases}
        onEdit={openPurchase}
        onView={openPurchase}
        onDeactivate={(purchase) => void deactivatePurchaseFromList(purchase)}
      />
    </GridPage>
  );
}
